import { QueryKey, queryOptions, useQuery } from "@tanstack/react-query";

import { drillBlastReferenceAjax } from "../../ajax/DrillBlastReferenceAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { PagedResult } from "../../DTOs/Common/PagedResult";
import { DrillBitSizeDTO, DrillBlastLookupDTO } from "../../DTOs/DrillBlast/DrillBlastLookupDTO";
import { VocabularyList, vocabularyName } from "../../i18n/vocabulary";

export const LOOKUP_STALE_TIME = 5 * 60 * 1000;

// Note: a failed fetch throws rather than returning [], so offline it cannot replace the list the prefetch cached.
export const drillBlastQuery = <T,>(queryKey: QueryKey, what: string, load: () => Promise<T | ErrorResponse>, staleTime?: number) => queryOptions({
  queryKey,
  queryFn: async (): Promise<T> => {
    const res = await load();
    if (ErrorResponse.isErrorResponse(res)) throw new Error(`Failed to load ${what}`);
    return res;
  },
  ...(staleTime == null ? {} : { staleTime }),
});

export const lookupQuery = <T,>(queryKey: QueryKey, what: string, load: () => Promise<PagedResult<T> | ErrorResponse>, staleTime?: number) =>
  drillBlastQuery<T[]>(queryKey, what, async () => {
    const res = await load();
    return ErrorResponse.isErrorResponse(res) ? res : res.data;
  }, staleTime);

export const bitSizesQuery = lookupQuery<DrillBitSizeDTO>(['references', 'drillBlast', 'bitSizes'], 'the bit sizes',
  () => drillBlastReferenceAjax.getBitSizes(), LOOKUP_STALE_TIME);
export const holeTypesQuery = lookupQuery<DrillBlastLookupDTO>(['references', 'drillBlast', 'holeTypes'], 'the hole types',
  () => drillBlastReferenceAjax.getHoleTypes(), LOOKUP_STALE_TIME);
export const blastTypesQuery = lookupQuery<DrillBlastLookupDTO>(['references', 'drillBlast', 'blastTypes'], 'the blast types',
  () => drillBlastReferenceAjax.getBlastTypes(), LOOKUP_STALE_TIME);
export const geoDrillingTypesQuery = lookupQuery<DrillBlastLookupDTO>(['references', 'drillBlast', 'geoDrillingTypes'], 'the drilling types',
  () => drillBlastReferenceAjax.getGeoDrillingTypes(), LOOKUP_STALE_TIME);

export const namedFrom = (list: VocabularyList) => (rows: DrillBlastLookupDTO[]) =>
  rows.map(row => ({ ...row, name: vocabularyName(list, row.name) }));

const holeTypeNames = namedFrom("holeTypes");
const blastTypeNames = namedFrom("blastTypes");
const geoDrillingTypeNames = namedFrom("geoDrillingTypes");

export function useBitSizes() {
  return useQuery(bitSizesQuery).data ?? [];
}

export function useHoleTypes() {
  return useQuery({ ...holeTypesQuery, select: holeTypeNames }).data ?? [];
}

export function useBlastTypes() {
  return useQuery({ ...blastTypesQuery, select: blastTypeNames }).data ?? [];
}

export function useGeoDrillingTypes() {
  return useQuery({ ...geoDrillingTypesQuery, select: geoDrillingTypeNames }).data ?? [];
}
